import {
    useEffect,
    useState
} from "react";
import {
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    type NCoreUIKitIcon,
    NCoreUIKitTheme,
    Slider,
    Button,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    type LucideIcon,
    ChevronDown,
    SkipForward,
    ChevronUp,
    ListMusic,
    SkipBack,
    Ellipsis,
    Shuffle,
    Speaker,
    Repeat,
    Heart,
    Pause,
    Play
} from "lucide-react-native";
import {
    Circle,
    Rect,
    Path,
    Line,
    Svg,
    G
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

const ChevronDownIcon = createKitIcon(ChevronDown);

const ChevronUpIcon = createKitIcon(ChevronUp);

const HeartIcon = createKitIcon(Heart);

const EllipsisIcon = createKitIcon(Ellipsis);

const ShuffleIcon = createKitIcon(Shuffle);

const RepeatIcon = createKitIcon(Repeat);

const SpeakerIcon = createKitIcon(Speaker);

const ListMusicIcon = createKitIcon(ListMusic);

const createFilledIcon = (Source: LucideIcon): NCoreUIKitIcon => {
    const FilledIcon: NCoreUIKitIcon = ({
        color,
        size
    }) => {
        const {
            colors
        } = NCoreUIKitTheme.useContext();

        return <Source
            color={colors.content.icon[color]}
            fill={colors.content.icon[color]}
            size={size}
        />;
    };

    return FilledIcon;
};

const HeartFilledIcon = createFilledIcon(Heart);

const PlayFilledIcon = createFilledIcon(Play);

const PauseFilledIcon = createFilledIcon(Pause);

const SkipBackFilledIcon = createFilledIcon(SkipBack);

const SkipForwardFilledIcon = createFilledIcon(SkipForward);

type LineShape = {
    x1: number;
    x2: number;
    y1: number;
    y2: number;
};

const TRACK_SECONDS = 238;

const INITIAL_SECONDS = 102;

const SECOND = 1000;

const ART_VIEWBOX = "0 0 342 342";

const BRIDGE_LINES = [
    {
        x1: 74,
        x2: 74,
        y1: 96,
        y2: 186
    },
    {
        y1: 132,
        x1: 90,
        x2: 90,
        y2: 186
    },
    {
        x1: 106,
        x2: 106,
        y1: 156,
        y2: 186
    },
    {
        x1: 124,
        x2: 124,
        y1: 168,
        y2: 186
    },
    {
        x1: 254,
        x2: 254,
        y1: 96,
        y2: 186
    },
    {
        x1: 238,
        x2: 238,
        y1: 132,
        y2: 186
    },
    {
        x1: 222,
        x2: 222,
        y1: 156,
        y2: 186
    },
    {
        x1: 204,
        x2: 204,
        y1: 168,
        y2: 186
    }
];

const SUN_LINES = [
    {
        x1: 214,
        x2: 274,
        y1: 208,
        y2: 208
    },
    {
        x1: 222,
        x2: 266,
        y1: 220,
        y2: 220
    },
    {
        x1: 204,
        x2: 282,
        y1: 232,
        y2: 232
    },
    {
        x1: 226,
        x2: 262,
        y1: 246,
        y2: 246
    }
];

const WARM_LINES = [
    {
        y1: 206,
        x1: 52,
        x2: 96,
        y2: 206
    },
    {
        x2: 108,
        y1: 220,
        x1: 40,
        y2: 220
    },
    {
        y1: 234,
        x1: 62,
        x2: 90,
        y2: 234
    }
];

const BRIDGE_CABLE = "M0 176 Q 40 176 74 96 Q 112 170 164 176 Q 214 170 254 96 Q 290 176 342 176";

const stylesheet = StyleSheet.create({
    lyricsHeader: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    controls: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    device: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    times: {
        justifyContent: "space-between",
        flexDirection: "row"
    },
    art: {
        justifyContent: "flex-end",
        alignSelf: "center",
        overflow: "hidden",
        aspectRatio: 1,
        width: "100%"
    },
    page: {
        flexDirection: "column",
        flex: 1
    },
    column: {
        flexDirection: "column"
    },
    artYear: {
        position: "absolute",
        bottom: 0,
        right: 0
    },
    titleRow: {
        alignItems: "center",
        flexDirection: "row"
    },
    svg: {
        position: "absolute",
        left: 0,
        top: 0
    },
    screen: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    center: {
        alignItems: "center"
    },
    grow: {
        minWidth: 0,
        flex: 1
    }
});

const handlePress = () => undefined;

const formatTime = (value: number) => {
    const minutes = Math.floor(value / 60);

    const seconds = Math.floor(value % 60);

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const Music01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        avatarSizes,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        seconds,
        setSeconds
    ] = useState(INITIAL_SECONDS);

    const [
        isPlaying,
        setIsPlaying
    ] = useState(true);

    const [
        isLiked,
        setIsLiked
    ] = useState(true);

    const [
        isShuffle,
        setIsShuffle
    ] = useState(false);

    const [
        isRepeat,
        setIsRepeat
    ] = useState(false);

    const [
        isLyricsOpen,
        setIsLyricsOpen
    ] = useState(false);

    useEffect(() => {
        if(!isPlaying) {
            return undefined;
        }

        const timer = setInterval(() => setSeconds((current) => current >= TRACK_SECONDS ? 0 : current + 1), SECOND);

        return () => clearInterval(timer);
    }, [
        isPlaying
    ]);

    const {
        categorical
    } = colors.chart;

    const ink = colors.content.container.inverse;

    const handleSeek = (value: number | Array<number>) => {
        if(typeof value === "number") {
            setSeconds(Math.round(value));
        }
    };

    const renderLines = (lines: Array<LineShape>, stroke: string, strokeWidth: number, opacity?: number) => <G
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        opacity={opacity}
        stroke={stroke}
    >
        {
            lines.map((line) => <Line
                key={`${line.x1}-${line.y1}`}
                {...line}
            />)
        }
    </G>;

    const renderArt = () => <View
        accessibilityLabel={t("blocks.music01.artLabel")}
        style={[
            stylesheet.art,
            {
                maxWidth: shapeConfigs.dialogMaxWidth,
                borderRadius: radiuses.lg
            }
        ]}
        accessibilityRole="image"
    >
        <Svg
            style={stylesheet.svg}
            viewBox={ART_VIEWBOX}
            height="100%"
            width="100%"
        >
            <Rect
                fill={categorical[5]}
                height={342}
                width={342}
            />
            <Rect
                fill={categorical[3]}
                height={190}
                width={342}
            />
            <Rect
                fill={categorical[5]}
                opacity={0.55}
                height={110}
                width={342}
            />
            <Circle
                fill={categorical[4]}
                cx={244}
                cy={86}
                r={44}
            />
            <Circle
                stroke={categorical[4]}
                strokeWidth={1.5}
                opacity={0.6}
                fill="none"
                cx={244}
                cy={86}
                r={62}
            />
            <Circle
                stroke={categorical[4]}
                strokeWidth={1}
                opacity={0.35}
                fill="none"
                cx={244}
                cy={86}
                r={82}
            />
            <Rect
                fill={categorical[3]}
                opacity={0.55}
                height={152}
                width={342}
                y={190}
            />
            <Rect
                opacity={0.18}
                height={152}
                width={342}
                fill={ink}
                y={190}
            />
            <Rect
                width={342}
                height={5}
                fill={ink}
                y={186}
                x={0}
            />
            <Rect
                height={98}
                fill={ink}
                width={8}
                y={92}
                x={70}
            />
            <Rect
                height={98}
                fill={ink}
                width={8}
                x={250}
                y={92}
            />
            <Path
                strokeWidth={2.5}
                d={BRIDGE_CABLE}
                stroke={ink}
                fill="none"
            />
            {renderLines(BRIDGE_LINES, ink, 1.2, 0.8)}
            {renderLines(SUN_LINES, categorical[4], 3, 0.9)}
            {renderLines(WARM_LINES, categorical[2], 3)}
        </Svg>
        <View
            style={{
                padding: spaces.spacingLg
            }}
        >
            <Text
                variant="headlineMediumSize"
                style={{
                    color: ink
                }}
            >
                {t("blocks.music01.artTitleTop")}
            </Text>
            <Text
                variant="headlineMediumSize"
                style={{
                    color: ink
                }}
            >
                {t("blocks.music01.artTitleBottom")}
            </Text>
        </View>
        <Text
            style={[
                stylesheet.artYear,
                {
                    padding: spaces.spacingLg,
                    color: ink
                }
            ]}
            variant="monoMediumSize"
        >
            2026
        </Text>
    </View>;

    return <View
        style={[
            stylesheet.page,
            {
                backgroundColor: colors.content.container.default
            }
        ]}
    >
        <ScrollView
            contentContainerStyle={[
                stylesheet.screen,
                {
                    maxWidth: shapeConfigs.sheetMaxWidth,
                    padding: spaces.spacingMd,
                    gap: spaces.spacingLg
                }
            ]}
            style={stylesheet.grow}
        >
            <View
                style={stylesheet.header}
            >
                <Button
                    accessibilityLabel={t("blocks.music01.close")}
                    spreadBehaviour="free"
                    icon={ChevronDownIcon}
                    onPress={handlePress}
                    variant="ghost"
                    type="neutral"
                />
                <View
                    style={stylesheet.center}
                >
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.music01.fromPlaylist")}
                    </Text>
                    <Text
                        variant="labelMediumSize"
                        color="high"
                    >
                        {t("blocks.music01.playlist")}
                    </Text>
                </View>
                <Button
                    accessibilityLabel={t("blocks.music01.more")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={EllipsisIcon}
                    variant="ghost"
                    type="neutral"
                />
            </View>
            {renderArt()}
            <View
                style={[
                    stylesheet.titleRow,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={stylesheet.grow}
                >
                    <Text
                        variant="titleLargeSize"
                        numberOfLines={1}
                        color="high"
                    >
                        {t("blocks.music01.track")}
                    </Text>
                    <Text
                        variant="bodyLargeSize"
                        numberOfLines={1}
                        color="mid"
                    >
                        {t("blocks.music01.artist")}
                    </Text>
                </View>
                <Button
                    accessibilityLabel={isLiked ? t("blocks.music01.unlike") : t("blocks.music01.like")}
                    icon={isLiked ? HeartFilledIcon : HeartIcon}
                    onPress={() => setIsLiked(!isLiked)}
                    spreadBehaviour="free"
                    variant="ghost"
                    type="primary"
                />
            </View>
            <View
                style={[
                    stylesheet.column,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Slider
                    accessibilityLabel={t("blocks.music01.progress")}
                    formatValueText={formatTime}
                    tooltipBehaviour="dragging"
                    onValueChange={handleSeek}
                    max={TRACK_SECONDS}
                    value={seconds}
                    min={0}
                />
                <View
                    style={stylesheet.times}
                >
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {formatTime(seconds)}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {formatTime(TRACK_SECONDS)}
                    </Text>
                </View>
            </View>
            <View
                style={stylesheet.controls}
            >
                <Button
                    accessibilityLabel={t("blocks.music01.shuffle")}
                    type={isShuffle ? "primary" : "neutral"}
                    onPress={() => setIsShuffle(!isShuffle)}
                    spreadBehaviour="free"
                    icon={ShuffleIcon}
                    variant="ghost"
                />
                <Button
                    accessibilityLabel={t("blocks.music01.previous")}
                    onPress={() => setSeconds(0)}
                    icon={SkipBackFilledIcon}
                    spreadBehaviour="free"
                    variant="ghost"
                    type="neutral"
                    size="large"
                />
                <Button
                    accessibilityLabel={isPlaying ? t("blocks.music01.pause") : t("blocks.music01.play")}
                    icon={isPlaying ? PauseFilledIcon : PlayFilledIcon}
                    style={{
                        height: avatarSizes.large + spaces.spacingSm,
                        width: avatarSizes.large + spaces.spacingSm
                    }}
                    onPress={() => setIsPlaying(!isPlaying)}
                    iconSize={avatarSizes.xxSmall}
                    borderRadius={radiuses.full}
                    spreadBehaviour="free"
                    isCustomPadding={true}
                    variant="filled"
                    type="primary"
                />
                <Button
                    accessibilityLabel={t("blocks.music01.next")}
                    onPress={() => setSeconds(0)}
                    icon={SkipForwardFilledIcon}
                    spreadBehaviour="free"
                    variant="ghost"
                    type="neutral"
                    size="large"
                />
                <Button
                    accessibilityLabel={t("blocks.music01.repeat")}
                    type={isRepeat ? "primary" : "neutral"}
                    onPress={() => setIsRepeat(!isRepeat)}
                    spreadBehaviour="free"
                    icon={RepeatIcon}
                    variant="ghost"
                />
            </View>
            <View
                style={stylesheet.device}
            >
                <Button
                    title={t("blocks.music01.device")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={SpeakerIcon}
                    variant="ghost"
                    type="primary"
                    size="small"
                />
                <Button
                    accessibilityLabel={t("blocks.music01.queue")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={ListMusicIcon}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
            </View>
            <Card
                variant="filled"
            >
                <Card.Content>
                    <View
                        style={[
                            stylesheet.column,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <View
                            style={stylesheet.lyricsHeader}
                        >
                            <Text
                                variant="labelMediumSize"
                                color="mid"
                            >
                                {t("blocks.music01.lyrics")}
                            </Text>
                            <Button
                                accessibilityLabel={isLyricsOpen ? t("blocks.music01.lyricsClose") : t("blocks.music01.lyricsOpen")}
                                icon={isLyricsOpen ? ChevronDownIcon : ChevronUpIcon}
                                onPress={() => setIsLyricsOpen(!isLyricsOpen)}
                                spreadBehaviour="free"
                                variant="ghost"
                                type="neutral"
                                size="small"
                            />
                        </View>
                        <Text
                            variant="titleMediumSize"
                            color="high"
                        >
                            {t("blocks.music01.lyricsLine1")}
                        </Text>
                        <Text
                            variant="titleMediumSize"
                            color="mid"
                        >
                            {t("blocks.music01.lyricsLine2")}
                        </Text>
                        {
                            isLyricsOpen ?
                                <>
                                    <Text
                                        variant="titleMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.music01.lyricsLine3")}
                                    </Text>
                                    <Text
                                        variant="titleMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.music01.lyricsLine4")}
                                    </Text>
                                </>
                            :
                                null
                        }
                    </View>
                </Card.Content>
            </Card>
        </ScrollView>
    </View>;
};
export default Music01;
